import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CreditPolicyRecord, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-credit-policies',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('sales.credit.policies.title')" [description]="t('sales.credit.policies.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('sales.filter.hide') : t('sales.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>
          @if (showFilters()) {
            <div class="filters">
              <label><span>{{ t('sales.credit.fields.code') }}</span><input pInputText [(ngModel)]="filters.code" (ngModelChange)="applyFilters()" /></label>
              <label><span>{{ t('sales.credit.fields.name') }}</span><input pInputText [(ngModel)]="filters.name" (ngModelChange)="applyFilters()" /></label>
              <label>
                <span>{{ t('sales.credit.fields.scopeType') }}</span>
                <p-select [options]="scopeOptions" optionValue="value" [(ngModel)]="filters.scopeType" [showClear]="true" appendTo="body" (ngModelChange)="applyFilters()">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          }
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.credit.fields.code') }}</th>
                <th>{{ t('sales.credit.fields.name') }}</th>
                <th>{{ t('sales.credit.fields.scopeType') }}</th>
                <th>{{ t('sales.credit.fields.defaultLimit') }}</th>
                <th>{{ t('sales.credit.fields.priority') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td>{{ row.code }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button></td>
                <td>{{ scopeLabel(row.scopeType) }}</td>
                <td>{{ row.defaultCreditLimit ?? '-' }}</td>
                <td>{{ row.priority }}</td>
                <td>{{ row.isActive ? t('sales.credit.active') : t('sales.credit.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class SalesCreditPoliciesComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly sourceRows = signal<CreditPolicyRecord[]>([]);
  readonly rows = signal<CreditPolicyRecord[]>([]);
  readonly selectedRows = signal<CreditPolicyRecord[]>([]);
  readonly filters = { code: '', name: '', scopeType: '' as string | null };

  readonly scopeOptions = [
    { value: 'Global', labelKey: 'sales.credit.scopes.global' },
    { value: 'Channel', labelKey: 'sales.credit.scopes.channel' },
    { value: 'Customer', labelKey: 'sales.credit.scopes.customer' }
  ];

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listCreditPolicies().subscribe({
      next: policies => { this.sourceRows.set(policies); this.applyFilters(); this.loading.set(false); },
      error: () => { this.sourceRows.set([]); this.rows.set([]); this.error.set(this.t('sales.credit.policies.errorLoad')); this.loading.set(false); }
    });
  }

  applyFilters(): void {
    const code = this.filters.code.trim().toLowerCase();
    const name = this.filters.name.trim().toLowerCase();
    const scopeType = this.filters.scopeType;
    this.rows.set(this.sourceRows().filter(row =>
      (!code || row.code.toLowerCase().includes(code)) &&
      (!name || row.name.toLowerCase().includes(name)) &&
      (!scopeType || row.scopeType === scopeType)));
  }

  scopeLabel(scopeType: string): string {
    return this.localization.translate(`sales.credit.scopes.${scopeType.charAt(0).toLowerCase()}${scopeType.slice(1)}`);
  }

  onSelectionChange(selection: CreditPolicyRecord[] | CreditPolicyRecord | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openCreate(): void { void this.router.navigate(['/sales/credit-policies/new']); }
  openSelected(): void { const row = this.selectedRows()[0]; if (row) this.openDetail(row.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/credit-policies', id]); }
  t(key: string): string { return this.localization.translate(key); }
}
